import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import {
  ReactComponentStaticProps,
  ReactDefaultProps,
} from "../../types/react";
import { Button, ButtonProps, ButtonDefaultTagName } from "../Button";
import { Icon, IconType, IconProps, IconPropTypes } from "../Icon";
import { Indicator } from "../Indicator";

import styles from "./MenuButton.scss";

type MenuButtonBaseProps<Tag extends React.ElementType> = {
  tag?: Tag;
  align?: "horizontal" | "vertical";
  icon: IconType;
  iconProps?: Omit<IconProps, "icon">;
  indicatorContent?: React.ReactNode | React.ReactNode[];
  /** keeps size equal regardless of viewport */
  equalSize?: boolean;
  customClass?: string;
};

export const MenuButtonDefaultTagName = ButtonDefaultTagName;
export type MenuButtonDefaultTag = typeof MenuButtonDefaultTagName;

export type MenuButtonProps<
  Tag extends React.ElementType = MenuButtonDefaultTag
> = MenuButtonBaseProps<Tag> &
  Omit<ButtonProps<Tag>, keyof MenuButtonBaseProps<Tag>>;

const defaultProps = {
  tag: MenuButtonDefaultTagName,
  align: "horizontal",
  indicatorContent: undefined,
  equalSize: false,
  customClass: undefined,
  iconProps: undefined,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  Omit<MenuButtonBaseProps<MenuButtonDefaultTag>, "icon">,
  typeof defaultProps
> = defaultProps;

const MenuButtonWithRef = <Tag extends React.ElementType>(
  {
    children,
    icon,
    align = defaultProps.align,
    indicatorContent = defaultProps.indicatorContent,
    equalSize = defaultProps.equalSize,
    customClass = defaultProps.customClass,
    iconProps = defaultProps.iconProps,
    ...props
  }: Omit<MenuButtonProps<Tag>, "ref">,
  ref: React.ComponentPropsWithRef<Tag>["ref"]
): JSX.Element => {
  const classes = classNames(
    styles.menuButton,
    {
      [styles.equalSize]: equalSize,
      [styles.vertical]: align === "vertical",
      [Button.Variant.labeledVertical]: align === "vertical",
    },
    customClass
  );

  return (
    <Button
      ref={ref}
      variants={[Button.Variant.bare]}
      customClass={classes}
      {...props}
    >
      {indicatorContent ? (
        <Indicator
          content={indicatorContent}
          style={{
            right: align === "horizontal" ? 7 : 2,
            top: align === "horizontal" ? -2 : -2,
            color: "white",
            background: "rgb(70, 168, 0)",
          }}
        >
          <Icon
            icon={icon}
            size={Icon.Size.parent}
            {...iconProps}
            wrapperProps={{
              ...(iconProps && iconProps.wrapperProps),
              className: styles.icon,
            }}
          />
        </Indicator>
      ) : (
        <Icon
          icon={icon}
          size={Icon.Size.parent}
          {...iconProps}
          wrapperProps={{
            ...(iconProps && iconProps.wrapperProps),
            className: styles.icon,
          }}
        />
      )}
      {children}
    </Button>
  );
};

export const MenuButton =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.forwardRef(MenuButtonWithRef) as (<
    Tag extends React.ElementType = MenuButtonDefaultTag
  >(
    props: MenuButtonProps<Tag>
  ) => JSX.Element) &
    ReactComponentStaticProps<MenuButtonProps>;

MenuButton.displayName = "MenuButton";

const { icon: removeIcon, ...extractedIconProps } = IconPropTypes;
const iconProps = extractedIconProps as Omit<typeof IconPropTypes, "icon">;

MenuButton.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  align: PropTypes.oneOf(["horizontal", "vertical"]),
  icon: PropTypes.elementType.isRequired as PropTypes.Validator<IconType>,
  iconProps: PropTypes.shape(iconProps) as PropTypes.Validator<IconProps>,
  indicatorContent: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  equalSize: PropTypes.bool,
  customClass: PropTypes.string,
};

MenuButton.defaultProps = validatedDefaultProps;

export default MenuButton;
